import { GraphQLError, GraphQLErrorExtensions } from "graphql";

export const BUSINESS_ERROR_PLACEHOLDER_MESSAGE = "Unexpected error occurred";

interface BusinessErrorCatalogEntry<TDetails = never> {
	httpStatus: number;
	message: string;
	_details?: TDetails;
}

export type NonEmptyArray<T> = [T, ...T[]];

export type NotFoundEntity =
	| "item"
	| "location"
	| "master_item"
	| "menu"
	| "order"
	| "receipt"
	| "receipt_config"
	| "resource"
	| "supplier"
	| "supplier_order"
	| "role"
	| "venue";

export type RequiredField =
	| "cancelled_reason"
	| "customer"
	| "note"
	| "orderId"
	| "orders"
	| "payment_types"
	| "prepared_by"
	| "processed_by";

export type OneOfField =
	| "deviceId"
	| "input"
	| "offlineInput"
	| "orderData"
	| "orderId"
	| "ref"
	| "venueId";

export type ExclusiveField = Extract<
	OneOfField,
	"input" | "offlineInput" | "orderData" | "orderId"
>;

export type ExistingEntity = "customer" | "role" | "venue";
export type ExistingField =
	| "phone_number"
	| "uid"
	| "venue_issued_id"
	| "name"
	| "username";

export const businessErrorCatalog = {
	AMOUNT_MUST_NOT_BE_ZERO: {
		httpStatus: 400,
		message: "Amount must not be 0",
	},
	BAD_REQUEST: {
		httpStatus: 400,
		message: "Mismatch params, routes, or payload",
	},
	CLOUDBEDS_REQUEST_FAILED: {
		httpStatus: 502,
		message: "Cloudbeds request failed",
		_details: {} as {
			reason?: string;
		},
	},
	CLOUDBEDS_CONNECTION_REQUIRED: {
		httpStatus: 400,
		message: "Cloudbeds is not currently connected",
	},
	CLOUDBEDS_CONNECT_URL_UNAVAILABLE: {
		httpStatus: 502,
		message: "Failed to get Cloudbeds connect URL",
	},
	CLOUDBEDS_INTEGRATION_NOT_ENABLED: {
		httpStatus: 400,
		message: "Cloudbeds integration is not enabled for this venue",
	},
	CLOUDBEDS_INTEGRATION_INFO_MISSING: {
		httpStatus: 400,
		message: "Cloudbeds integration info missing",
	},
	CLOUDBEDS_PROPERTY_RESOURCE_NOT_FOUND: {
		httpStatus: 502,
		message: "No property resource found in Cloudbeds response",
	},
	DATE_RANGE_EXCEEDED: {
		httpStatus: 400,
		message: "Date range cannot exceed allowed limit",
		_details: {} as {
			maxMonths: number;
		},
	},
	DUPLICATE_IDEMPOTENCY_KEY: {
		httpStatus: 409,
		message: "An order with this idempotency key was already created",
		_details: {} as {
			idempotency_key: string;
			existing_order_id: string;
		},
	},
	EITHER_VAT_OR_SERVICE_CHARGE_REQUIRED: {
		httpStatus: 400,
		message: "Either vat, service_charge, or service_charge_bd is required",
	},
	ENTITY_ALREADY_EXISTS: {
		httpStatus: 409,
		message: "Entity already exists",
		_details: {} as {
			entity: ExistingEntity;
			field: ExistingField;
			value?: string;
		},
	},
	FIELD_REQUIRED: {
		httpStatus: 400,
		message: "Field is required",
		_details: {} as {
			field: RequiredField;
		},
	},
	INCORRECT_LOGIN_DETAILS: {
		httpStatus: 401,
		message: "Incorrect login details",
	},
	INCORRECT_PAYMENTS_AMOUNT: {
		httpStatus: 400,
		message: "Incorrect payments amount",
	},
	ITEMS_QUANTITY_MISMATCH: {
		httpStatus: 409,
		message:
			"Item quantity mismatch detected. You may be viewing an outdated version of the order. Please reload the page and try again.",
	},
	INTERNAL_ERROR: {
		httpStatus: 500,
		message: "Something went wrong",
	},
	INVALID_ORDER_STATUS: {
		httpStatus: 400,
		message: "Invalid order status",
	},
	INVALID_PARAMETERS: {
		httpStatus: 400,
		message: "Invalid parameters",
	},
	INVALID_PRE_SIGNED_URL_TYPE: {
		httpStatus: 400,
		message: "Invalid pre-signed URL type",
	},
	INVALID_TRANSACTION_DATA: {
		httpStatus: 400,
		message: "Invalid transaction data",
	},
	INVALID_VENUE_SHIFT: {
		httpStatus: 400,
		message: "Shift not found in venue configuration",
		_details: {} as {
			shiftTitle: string;
		},
	},
	LOCATION_LOCKED_BY_OTHER_USER: {
		httpStatus: 409,
		message: "Location is in use by another user",
		_details: {} as {
			locationId: string;
			ownerRoleId: string | null;
		},
	},
	LOCATION_NOT_FULLY_CLOSED_YET: {
		httpStatus: 400,
		message: "Location not fully closed yet",
	},
	LOCATION_STILL_HAS_ACTIVE_ORDERS: {
		httpStatus: 400,
		message: "Location still has active orders",
	},
	MASTERS_CANNOT_CONTAIN_OTHER_MASTER_ITEMS: {
		httpStatus: 400,
		message: "Masters cannot contain other master items",
	},
	MOVED_ITEMS_REQUIRED: {
		httpStatus: 400,
		message: "Must select at least one order, or one item to move",
	},
	MOVED_QUANTITY_EXCEEDS_AVAILABLE_ITEM_QUANTITY: {
		httpStatus: 400,
		message: "Moved quantity exceeds available item quantity",
	},
	MUST_SELECT_AT_LEAST_ONE_ORDER_OR_ITEM_TO_SPLIT: {
		httpStatus: 400,
		message: "Must select at least one order, or one item to split",
	},
	MUTUALLY_EXCLUSIVE_FIELDS: {
		httpStatus: 400,
		message: "Only one of these fields is allowed",
		_details: {} as {
			fields: NonEmptyArray<ExclusiveField>;
		},
	},
	NO_ITEM_TO_PRINT: {
		httpStatus: 400,
		message: "No item to print",
	},
	NO_ORDERS_FOUND_FOR_RECEIPT: {
		httpStatus: 404,
		message: "No orders found for receipt",
	},
	NOT_FOUND: {
		httpStatus: 404,
		message: "Resource not found",
		_details: {} as {
			entity: NotFoundEntity;
			id?: string;
			ids?: string[];
			parentEntity?: string;
			parentId?: string;
		},
	},
	NO_UNPAID_ORDERS_REMAIN: {
		httpStatus: 400,
		message: "No unpaid orders remain",
	},
	ONE_OF_FIELDS_REQUIRED: {
		httpStatus: 400,
		message: "At least one field is required",
		_details: {} as {
			fields: NonEmptyArray<OneOfField>;
		},
	},
	ONLY_QR_ORDERING_ALLOWED: {
		httpStatus: 400,
		message: "Only QR ordering is allowed",
	},
	ORDERS_ALREADY_PAID: {
		httpStatus: 409,
		message: "One or more of the given orders was already paid for",
	},
	ORDER_SNAPSHOT_STALE: {
		httpStatus: 409,
		message: "Order changed since it was loaded. Reload and try again.",
	},
	ORDER_ALREADY_CANCELLED: {
		httpStatus: 409,
		message: "Cannot modify a cancelled order",
	},
	CANNOT_ADD_ITEMS_TO_PAID_ORDER: {
		httpStatus: 409,
		message: "Cannot add items to a paid order",
	},
	ORDERS_NOT_PREPARED: {
		httpStatus: 400,
		message: "One or more orders have not been prepared yet",
	},
	PAYWAY_PAYMENT_FAILED: {
		httpStatus: 400,
		message: "PayWay payment failed",
	},
	AI_NOT_CONFIGURED: {
		httpStatus: 503,
		message: "AI provider is not configured",
	},
	MAINTENANCE_MODE: {
		httpStatus: 503,
		message: "The system is under maintenance. Please try again later.",
		_details: {} as {
			message?: string;
		},
	},
	PAYWAY_POSTBACK_NOT_RECEIVED: {
		httpStatus: 502,
		message: "PayWay did not confirm the payment in time",
	},
	PAYWAY_REQUEST_FAILED: {
		httpStatus: 502,
		message:
			"PayWay could not start this payment. Please try again or choose another payment method.",
	},
	PAYWAY_TRANSACTION_PENDING: {
		httpStatus: 409,
		message: "PayWay transaction is still pending",
	},
	REPORTS_AVAILABILITY_MISSED: {
		httpStatus: 403,
		message: "Reports are currently disabled for operations",
	},
	REPORTS_DISABLED: {
		httpStatus: 403,
		message: "Reports have been temporarily disabled",
	},
	RATE_LIMITED: {
		httpStatus: 429,
		message: "Too many requests, please try again shortly",
	},
	ROOM_MASTER_INQUIRY_FAILED: {
		httpStatus: 502,
		message:
			"Unable to look up the specified room number, please check with your Room Master system admin. This may be due to the following reasons:\n\t1 - The Folio ID has been updated and does not match with the transaction's original Folio ID.\n\t2 - The room# has been updated and does not match with the transaction's original room#.\n\t3 - The room you are trying to post the transaction to is not checked in the Room Master System.",
	},
	ROOM_MASTER_POSTING_FAILED: {
		httpStatus: 502,
		message:
			"Unable to post transaction to Room Master, please check with your Room Master system admin. This may be due to the following reasons:\n\t1 - The Folio ID has been updated and does not match with the transaction's original Folio ID.\n\t2 - The room# has been updated and does not match with the transaction's original room#.\n\t3 - The room you are trying to post the transaction to is not checked in the Room Master System.",
	},
	ROOM_MASTER_PROPERTY_ID_NOT_CONFIGURED: {
		httpStatus: 400,
		message: "Room Master property ID not configured",
	},
	SOLD_OUT_ITEMS: {
		httpStatus: 409,
		message: "Some items are sold out",
		_details: {} as {
			titles: string[];
		},
	},
	TOKEN_EXPIRED: {
		httpStatus: 401,
		message: "Expired session",
	},
	UNAUTHORIZED: {
		httpStatus: 401,
		message: "Not authorized",
	},
	VALIDATION_ERROR: {
		httpStatus: 400,
		message: "Validation failed",
		_details: {} as {
			reasons: string[];
		},
	},
} satisfies Record<string, BusinessErrorCatalogEntry<any>>;

export type BusinessErrorCatalog = typeof businessErrorCatalog;
export type BusinessErrorCode = keyof BusinessErrorCatalog;
export type BusinessErrorDetails<TCode extends BusinessErrorCode> =
	BusinessErrorCatalog[TCode] extends { _details: infer TDetails }
		? TDetails
		: never;

type BusinessErrorOptions<TCode extends BusinessErrorCode> = [
	BusinessErrorDetails<TCode>,
] extends [never]
	? {
			cause?: unknown;
		}
	: {
			details: BusinessErrorDetails<TCode>;
			cause?: unknown;
		};

export interface BusinessErrorPayload<
	TCode extends BusinessErrorCode = BusinessErrorCode,
> {
	code: TCode;
	details?: BusinessErrorDetails<TCode>;
}

export type BusinessErrorPayloadByCode = {
	[TCode in BusinessErrorCode]: BusinessErrorPayload<TCode>;
}[BusinessErrorCode];

const externalFaultCodes = new Set<BusinessErrorCode>([
	"CLOUDBEDS_REQUEST_FAILED",
	"CLOUDBEDS_CONNECT_URL_UNAVAILABLE",
	"CLOUDBEDS_PROPERTY_RESOURCE_NOT_FOUND",
	"PAYWAY_POSTBACK_NOT_RECEIVED",
	"PAYWAY_REQUEST_FAILED",
	"ROOM_MASTER_INQUIRY_FAILED",
	"ROOM_MASTER_POSTING_FAILED",
	"AI_NOT_CONFIGURED",
]);

export const isServedFaultError = (
	payload: Pick<BusinessErrorPayloadByCode, "code"> | null,
): boolean =>
	payload === null ||
	(businessErrorCatalog[payload.code].httpStatus >= 500 &&
		!externalFaultCodes.has(payload.code));

export class BusinessError<
	TCode extends BusinessErrorCode = BusinessErrorCode,
> extends Error {
	code: TCode;
	httpStatus: number;
	details?: BusinessErrorDetails<TCode>;
	override cause?: unknown;

	constructor(code: TCode, options?: BusinessErrorOptions<TCode>) {
		super(businessErrorCatalog[code].message);
		this.name = "BusinessError";
		this.code = code;
		this.httpStatus = businessErrorCatalog[code].httpStatus;
		if (options && "details" in options) {
			this.details = options.details;
		}
		if (options?.cause !== undefined) {
			this.cause = options.cause;
		}
		this.message = getBusinessErrorDisplayMessage({
			code: this.code,
			...(this.details !== undefined ? { details: this.details } : {}),
		} as BusinessErrorPayloadByCode);
	}
}

export class MessageBusinessError extends BusinessError {
	constructor(message: string) {
		const mapped =
			businessErrorFromLegacyMessage(message) ??
			new BusinessError("INTERNAL_ERROR", { cause: message });

		super(
			mapped.code,
			mapped.details === undefined
				? undefined
				: ({ details: mapped.details } as any),
		);
		this.cause = mapped.cause;
	}
}

export const isBusinessErrorCode = (
	value: unknown,
): value is BusinessErrorCode =>
	typeof value === "string" && value in businessErrorCatalog;

export const isBusinessError = (value: unknown): value is BusinessError =>
	value instanceof BusinessError;

export const getBusinessErrorMessage = <TCode extends BusinessErrorCode>(
	code: TCode,
): string => businessErrorCatalog[code].message;

const titleCaseWords = (value: string) =>
	value
		.split("_")
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(" ");

const formatFieldLabel = (
	field: RequiredField | OneOfField | ExclusiveField,
) => {
	switch (field) {
		case "cancelled_reason":
			return "Cancel reason";
		case "customer":
			return "Customer";
		case "deviceId":
			return "deviceId";
		case "input":
			return "input";
		case "note":
			return "Order note";
		case "offlineInput":
			return "offlineInput";
		case "orderData":
			return "orderData";
		case "orderId":
			return "orderId";
		case "orders":
			return "Orders";
		case "payment_types":
			return "Payment types";
		case "prepared_by":
			return "prepared_by";
		case "processed_by":
			return "processed_by";
		case "ref":
			return "ref";
		case "venueId":
			return "venueId";
	}
};

const formatEntityLabel = (entity: NotFoundEntity | ExistingEntity) => {
	switch (entity) {
		case "master_item":
			return "Master item";
		case "receipt_config":
			return "Receipt config";
		default:
			return titleCaseWords(entity);
	}
};

const formatFieldList = (fields: readonly string[]) => fields.join(" or ");

export const getBusinessErrorDisplayMessage = (
	error: BusinessError | BusinessErrorPayloadByCode,
): string => {
	const payload = isBusinessError(error)
		? toBusinessErrorPayload(error)
		: error;

	switch (payload.code) {
		case "CLOUDBEDS_REQUEST_FAILED":
			return payload.details?.reason ?? getBusinessErrorMessage(payload.code);
		case "MAINTENANCE_MODE":
			return payload.details?.message ?? getBusinessErrorMessage(payload.code);
		case "DATE_RANGE_EXCEEDED":
			return payload.details?.maxMonths !== undefined
				? `Date range cannot exceed ${payload.details.maxMonths} months`
				: getBusinessErrorMessage(payload.code);
		case "ENTITY_ALREADY_EXISTS":
			if (!payload.details) return getBusinessErrorMessage(payload.code);
			if (
				payload.details.entity === "customer" &&
				payload.details.field === "uid"
			) {
				return "Customer already exists with this UID";
			}
			if (
				payload.details.entity === "customer" &&
				payload.details.field === "phone_number"
			) {
				return "Customer already exists with this phone number";
			}
			if (
				payload.details.entity === "role" &&
				payload.details.field === "venue_issued_id"
			) {
				return "User with the same venue issued id already exists";
			}
			if (payload.details.value) {
				return `${formatEntityLabel(payload.details.entity)} already exists for ${payload.details.value}`;
			}
			return `${formatEntityLabel(payload.details.entity)} already exists`;
		case "FIELD_REQUIRED":
			return payload.details?.field
				? `${formatFieldLabel(payload.details.field)} is required`
				: getBusinessErrorMessage(payload.code);
		case "INVALID_VENUE_SHIFT":
			return payload.details?.shiftTitle
				? `Shift "${payload.details.shiftTitle}" not found in venue configuration`
				: getBusinessErrorMessage(payload.code);
		case "LOCATION_LOCKED_BY_OTHER_USER":
			return payload.details?.locationId
				? `Location ${payload.details.locationId} is in use by another user`
				: getBusinessErrorMessage(payload.code);
		case "MUTUALLY_EXCLUSIVE_FIELDS":
			return payload.details?.fields?.length
				? `Only one of ${formatFieldList(payload.details.fields)} is allowed`
				: getBusinessErrorMessage(payload.code);
		case "NOT_FOUND":
			if (
				payload.details?.entity === "master_item" &&
				payload.details.ids?.length
			) {
				return `Master items not found: ${payload.details.ids.join(", ")}`;
			}
			if (
				payload.details?.entity === "receipt_config" &&
				payload.details.parentEntity === "venue" &&
				payload.details.parentId
			) {
				return `Receipt config not found for venue ${payload.details.parentId}`;
			}
			if (payload.details?.entity) {
				if (payload.details.id) {
					return `${formatEntityLabel(payload.details.entity)} not found: ${payload.details.id}`;
				}
				return `${formatEntityLabel(payload.details.entity)} not found`;
			}
			return getBusinessErrorMessage(payload.code);
		case "ONE_OF_FIELDS_REQUIRED":
			return payload.details?.fields?.length
				? `Either ${formatFieldList(payload.details.fields)} is required`
				: getBusinessErrorMessage(payload.code);
		case "SOLD_OUT_ITEMS":
			return payload.details?.titles?.length
				? `Some items are sold out: ${payload.details.titles.join(", ")}`
				: getBusinessErrorMessage(payload.code);
		case "VALIDATION_ERROR":
			return payload.details?.reasons?.length
				? payload.details.reasons.join("\n")
				: getBusinessErrorMessage(payload.code);
		default:
			return getBusinessErrorMessage(payload.code);
	}
};

export const normalizeUnknownError = (error: unknown): BusinessError => {
	if (isBusinessError(error)) return error;
	const validationReasons = (error as { errors?: unknown })?.errors;
	if (
		Array.isArray(validationReasons) &&
		validationReasons.every((reason) => typeof reason === "string")
	) {
		return new BusinessError("VALIDATION_ERROR", {
			details: { reasons: validationReasons },
			cause: error,
		});
	}
	if (error instanceof GraphQLError) {
		const code = error.extensions?.code;
		if (isBusinessErrorCode(code)) {
			return fromBusinessErrorPayload({
				code,
				details: error.extensions?.details as BusinessErrorDetails<typeof code>,
			});
		}
		const fromMessage = businessErrorFromLegacyMessage(error.message);
		if (fromMessage) return fromMessage;
	}
	if (error instanceof Error) {
		const fromMessage = businessErrorFromLegacyMessage(error.message);
		if (fromMessage) return fromMessage;
	}
	return new BusinessError("INTERNAL_ERROR", { cause: error });
};

export const toBusinessErrorPayload = (
	error: BusinessError,
): BusinessErrorPayloadByCode =>
	({
		code: error.code,
		...(error.details !== undefined ? { details: error.details } : {}),
	}) as BusinessErrorPayloadByCode;

export const toGraphQLFormattedError = (error: BusinessError): GraphQLError =>
	new GraphQLError(BUSINESS_ERROR_PLACEHOLDER_MESSAGE, {
		extensions: toBusinessErrorPayload(
			error,
		) as unknown as GraphQLErrorExtensions,
	});

export const toRestErrorBody = (error: BusinessError) => ({
	message: BUSINESS_ERROR_PLACEHOLDER_MESSAGE,
	error: toBusinessErrorPayload(error),
});

const getPayloadDetails = <TCode extends BusinessErrorCode>(
	details: BusinessErrorDetails<TCode> | undefined,
) => {
	if (details === undefined) return undefined;
	return { details } as Extract<
		BusinessErrorOptions<TCode>,
		{ details: unknown }
	>;
};

export const fromBusinessErrorPayload = <TCode extends BusinessErrorCode>(
	payload: BusinessErrorPayload<TCode>,
): BusinessError<TCode> =>
	new BusinessError(payload.code, getPayloadDetails(payload.details));

export const parseGraphQLBusinessError = (
	error: unknown,
): BusinessErrorPayloadByCode | null => {
	const extensions = (error as { extensions?: unknown })?.extensions as
		| BusinessErrorPayloadByCode
		| undefined;
	if (!extensions?.code || !isBusinessErrorCode(extensions.code)) return null;
	return extensions;
};

export const parseRestBusinessError = (
	value: unknown,
): BusinessErrorPayloadByCode | null => {
	const payload = (value as { error?: BusinessErrorPayloadByCode })?.error;
	if (!payload?.code || !isBusinessErrorCode(payload.code)) return null;
	return payload;
};

const businessErrorFromLegacyMessage = (
	message: string,
): BusinessError | null => {
	const exactMatches: Partial<
		Record<string, BusinessErrorCode | (() => BusinessError)>
	> = {
		"Amount must not be 0": "AMOUNT_MUST_NOT_BE_ZERO",
		"Cancel reason is required": () =>
			new BusinessError("FIELD_REQUIRED", {
				details: { field: "cancelled_reason" },
			}),
		"Cloudbeds integration info missing": "CLOUDBEDS_INTEGRATION_INFO_MISSING",
		"Cloudbeds integration is not enabled for this venue":
			"CLOUDBEDS_INTEGRATION_NOT_ENABLED",
		"Cloudbeds is not currently connected": "CLOUDBEDS_CONNECTION_REQUIRED",
		"Cloudbeds request failed": "CLOUDBEDS_REQUEST_FAILED",
		"Customer already exists with this UID": () =>
			new BusinessError("ENTITY_ALREADY_EXISTS", {
				details: { entity: "customer", field: "uid" },
			}),
		"Customer already exists with this phone number": () =>
			new BusinessError("ENTITY_ALREADY_EXISTS", {
				details: { entity: "customer", field: "phone_number" },
			}),
		"Customer is required": () =>
			new BusinessError("FIELD_REQUIRED", {
				details: { field: "customer" },
			}),
		"Date range cannot exceed allowed limit": "DATE_RANGE_EXCEEDED",
		"Either input or offlineInput is required": () =>
			new BusinessError("ONE_OF_FIELDS_REQUIRED", {
				details: { fields: ["input", "offlineInput"] },
			}),
		"Either orderData or orderId is required": () =>
			new BusinessError("ONE_OF_FIELDS_REQUIRED", {
				details: { fields: ["orderData", "orderId"] },
			}),
		"Either orderId or ref is required": () =>
			new BusinessError("ONE_OF_FIELDS_REQUIRED", {
				details: { fields: ["orderId", "ref"] },
			}),
		"Either venueId or deviceId is required": () =>
			new BusinessError("ONE_OF_FIELDS_REQUIRED", {
				details: { fields: ["venueId", "deviceId"] },
			}),
		"Expired session": "TOKEN_EXPIRED",
		"Failed to get Cloudbeds connect URL": "CLOUDBEDS_CONNECT_URL_UNAVAILABLE",
		"Incorrect login details": "INCORRECT_LOGIN_DETAILS",
		"Incorrect payments amount": "INCORRECT_PAYMENTS_AMOUNT",
		"Items quantity mismatch, reload the page and try again":
			"ITEMS_QUANTITY_MISMATCH",
		"Invalid order status": "INVALID_ORDER_STATUS",
		"Invalid parameters": "INVALID_PARAMETERS",
		"Invalid pre-signed URL type": "INVALID_PRE_SIGNED_URL_TYPE",
		"Invalid transaction data": "INVALID_TRANSACTION_DATA",
		"Item not found": () =>
			new BusinessError("NOT_FOUND", {
				details: { entity: "item" },
			}),
		"Shift not found in venue configuration": "INVALID_VENUE_SHIFT",
		"Location is in use by another user": "LOCATION_LOCKED_BY_OTHER_USER",
		"Location not found": () =>
			new BusinessError("NOT_FOUND", {
				details: { entity: "location" },
			}),
		"Location not fully closed yet": "LOCATION_NOT_FULLY_CLOSED_YET",
		"Location still has active orders": "LOCATION_STILL_HAS_ACTIVE_ORDERS",
		"Masters cannot contain other master items":
			"MASTERS_CANNOT_CONTAIN_OTHER_MASTER_ITEMS",
		"Menu not found": () =>
			new BusinessError("NOT_FOUND", {
				details: { entity: "menu" },
			}),
		"Moved quantity exceeds available item quantity":
			"MOVED_QUANTITY_EXCEEDS_AVAILABLE_ITEM_QUANTITY",
		"Mismatch params, routes, or payload": "BAD_REQUEST",
		"Must select at least one order, or one item to move":
			"MOVED_ITEMS_REQUIRED",
		"Must select at least one order, or one item to split":
			"MUST_SELECT_AT_LEAST_ONE_ORDER_OR_ITEM_TO_SPLIT",
		"No item to print": "NO_ITEM_TO_PRINT",
		"No orders found for receipt": "NO_ORDERS_FOUND_FOR_RECEIPT",
		"No unpaid orders remain": "NO_UNPAID_ORDERS_REMAIN",
		"Not authorized": "UNAUTHORIZED",
		"Not found": () =>
			new BusinessError("NOT_FOUND", {
				details: { entity: "resource" },
			}),
		"One or more of the given orders was already paid for":
			"ORDERS_ALREADY_PAID",
		"One or more orders have not been prepared yet": "ORDERS_NOT_PREPARED",
		"Only QR ordering is allowed": "ONLY_QR_ORDERING_ALLOWED",
		"Only one of input or offlineInput is allowed": () =>
			new BusinessError("MUTUALLY_EXCLUSIVE_FIELDS", {
				details: { fields: ["input", "offlineInput"] },
			}),
		"Only one of orderData or orderId is allowed": () =>
			new BusinessError("MUTUALLY_EXCLUSIVE_FIELDS", {
				details: { fields: ["orderData", "orderId"] },
			}),
		"Order ID is required": () =>
			new BusinessError("FIELD_REQUIRED", {
				details: { field: "orderId" },
			}),
		"Order note is required": () =>
			new BusinessError("FIELD_REQUIRED", {
				details: { field: "note" },
			}),
		"Order not found": () =>
			new BusinessError("NOT_FOUND", {
				details: { entity: "order" },
			}),
		"Orders are required": () =>
			new BusinessError("FIELD_REQUIRED", {
				details: { field: "orders" },
			}),
		"PayWay payment failed": "PAYWAY_PAYMENT_FAILED",
		"PayWay transaction is still pending": "PAYWAY_TRANSACTION_PENDING",
		"Payment types are required": () =>
			new BusinessError("FIELD_REQUIRED", {
				details: { field: "payment_types" },
			}),
		"Receipt config not found": () =>
			new BusinessError("NOT_FOUND", {
				details: { entity: "receipt_config" },
			}),
		"Reports are currently disabled for operations":
			"REPORTS_AVAILABILITY_MISSED",
		"Reports have been temporarily disabled": "REPORTS_DISABLED",
		"Requested resource not found": () =>
			new BusinessError("NOT_FOUND", {
				details: { entity: "resource" },
			}),
		"Room Master property ID not configured":
			"ROOM_MASTER_PROPERTY_ID_NOT_CONFIGURED",
		Unauthorized: "UNAUTHORIZED",
		tokenExpired: "TOKEN_EXPIRED",
		"User not found": () =>
			new BusinessError("NOT_FOUND", {
				details: { entity: "role" },
			}),
		"User with the same venue issued id already exists": () =>
			new BusinessError("ENTITY_ALREADY_EXISTS", {
				details: { entity: "role", field: "venue_issued_id" },
			}),
		"Venue not found": () =>
			new BusinessError("NOT_FOUND", {
				details: { entity: "venue" },
			}),
	};

	const exactMatch = exactMatches[message];
	if (typeof exactMatch === "function") return exactMatch();
	if (exactMatch) return new BusinessError(exactMatch);

	if (message.startsWith("Master items not found: ")) {
		const itemIds = message
			.replace("Master items not found: ", "")
			.split(",")
			.map((item) => item.trim())
			.filter(Boolean);
		return new BusinessError("NOT_FOUND", {
			details: { entity: "master_item", ids: itemIds },
		});
	}

	if (message.endsWith(" document not found")) {
		const resource = message.replace(" document not found", "");
		return new BusinessError("NOT_FOUND", {
			details: { entity: "resource", id: resource },
		});
	}

	return null;
};
